import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { compact, get, isArray, uniq } from 'lodash';
import { Tooltip } from '@mui/material';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { getAllGridConfiguration, removeConfiguration } from './_api';
import { getAllWorkplaces } from 'modules/Administration/Workplaces/_api';
import Header from 'components/Header/Header';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import { useWithTitle } from 'utils/hooks/useWithTitle';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useActions } from 'utils/hooks/useActions';
import { useLanguage } from 'utils/hooks/useLanguage';

const muiGridKey = 'gridConfigurationsMUI';
const url = '/administration/frontendSettings/GridConfigurationForm/';

const GridConfigurations: React.FC = () => {
  const { t } = useTranslation('GridConfigurations');
  useWithTitle();
  const navigate = useNavigate();
  const { confirmationData } = useAppInfo();
  const { toggleLoader } = useAppGlobals();
  const { storeConfirmationData } = useActions();
  const { currentLocale } = useLanguage();

  const [gridConfigurations, setGridConfigurations] = useState<any[]>([]);
  const onEntityEdit = useCallback(
    async (id: number) => {
      navigate(`${url}${id}`);
    },
    [navigate],
  );

  const loadEntity = useCallback(async () => {
    toggleLoader();
    let preparedWorkplaces: any[] = [];
    try {
      const [gridConfigurations, workplaces] = await Promise.all([
        getAllGridConfiguration(),
        getAllWorkplaces('db'),
      ]);
      if (workplaces && isArray(workplaces)) {
        preparedWorkplaces = workplaces.map((workplace) => ({
          id: workplace.id,
          label: get(
            get(workplace, 'text', '') ? JSON.parse(get(workplace, 'text', '')) : {},
            currentLocale,
            workplace.code,
          ),
        }));
      }
      if (gridConfigurations && isArray(gridConfigurations)) {
        setGridConfigurations(
          gridConfigurations.map((configuration) => {
            const targetGridTranslated = t(configuration.targetGrid.toLocaleLowerCase());
            const workplaceNames = configuration.workplaceIds.map((workplaceId: number) => {
              const workplace = preparedWorkplaces.find((wp) => wp.id === workplaceId);
              return workplace ? workplace.label : '';
            });
            return {
              ...configuration,
              targetGridTranslated,
              workplaceNames: workplaceNames.join(', '),
            };
          }),
        );
      } else {
        setGridConfigurations([]);
      }
    } catch (e) {
      console.debug(e);
    } finally {
      toggleLoader(false);
    }
  }, [currentLocale, t, toggleLoader]);

  const { onEntityRemove } = useEntityRemove(removeConfiguration, t, loadEntity);

  const clearAction = () => storeConfirmationData(null);

  const confirmAction = async () => {
    await onEntityRemove(get(confirmationData, 'id'));
    clearAction();
  };

  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqTargetGrids = useMemo(
    () =>
      uniq(compact(gridConfigurations.map((configuration) => configuration.targetGridTranslated))),
    [gridConfigurations],
  );

  const columns = useMemo(
    () =>
      reorderColumnsByGridSettings(
        injectColumnWidthsIntoColumns([
          {
            field: 'actions',
            headerName: t('Grid:actions'),
            type: 'actions',
            hideable: false,
            width: 120,
            renderCell: ({ row }: GridRenderCellParams) => {
              return (
                <>
                  <GridActionsCellItem
                    icon={
                      <Tooltip title={t('Grid:edit')}>
                        <EditIcon />
                      </Tooltip>
                    }
                    label={t('Grid:edit')}
                    onClick={() => onEntityEdit(get(row, 'id'))}
                    data-pw={`grid_configuration_edit_${row.id}`}
                  />
                  <GridActionsCellItem
                    icon={
                      <Tooltip title={t('Grid:delete')}>
                        <DeleteIcon />
                      </Tooltip>
                    }
                    label={t('Grid:delete')}
                    onClick={() => storeConfirmationData({ id: get(row, 'id') })}
                    data-pw={`grid_configuration_delete_${row.id}`}
                  />
                </>
              );
            },
          },
          { field: 'name', headerName: t('name') },
          {
            field: 'targetGridTranslated',
            headerName: t('targetGrid'),
            width: 180,
            type: 'singleSelect',
            valueOptions: uniqTargetGrids || [],
          },
          { field: 'workplaceNames', headerName: t('workplaceIds') },
          { field: 'priority', headerName: t('priority'), type: 'number', width: 100 },
        ]),
      ),
    [
      injectColumnWidthsIntoColumns,
      onEntityEdit,
      reorderColumnsByGridSettings,
      storeConfirmationData,
      t,
      uniqTargetGrids,
    ],
  );

  return (
    <>
      <Header
        title=""
        addUrl={`${url}create`}
        topMargin={true}
        dataPw="gridConfigurations-header-title"
      />
      <MuiGrid
        rows={gridConfigurations}
        columns={columns}
        initialSortMode={[{ field: 'name', sort: 'asc' }]}
        gridKey={muiGridKey}
      />

      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={confirmationData.title ? t(confirmationData.title) : t('Grid:confirmDelete')}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
    </>
  );
};

export default GridConfigurations;
